<script>
	import { regimenStore, currentPage } from '../store.js';
	import { navigate } from '../router.js';
	import { formDataStore } from '../components/store';
	import { onMount } from 'svelte';
	import { get } from 'svelte/store';
	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Chip from '../components/Chip.svelte';
	import EmptyState from '../components/EmptyState.svelte';
	import Stat from '../components/Stat.svelte';
  
export let goToRoutine;
// Parent passes a Svelte store containing recommendations
export let recommendations;
  
	// Filter state variables
	let crueltyFree = false;
	let pricePoint = 'any';
	let storePreference = '';
	let openAlternatives = null;
	// Specific-products mode: the person asked for these slots only, and the
	// engine checked each pick against the routine they already use.
	$: specificMode = Array.isArray(($formDataStore || {}).productTypes) && ($formDataStore || {}).productTypes.length > 0;
	$: checkedAgainstRoutine = Array.isArray(($formDataStore || {}).currentRoutine) && ($formDataStore || {}).currentRoutine.length > 0;

	// Presentation only: a one-line readout of the filter state, so the
	// collapsed filter bar still says what is currently applied.
	const PRICE_LABELS = { any: 'Any price', low: 'Under $50', medium: '$50 to $100', high: 'Over $100' };
	const STORE_LABELS = { '': 'All stores', sephora: 'Sephora', ulta: 'Ulta Beauty', amazon: 'Amazon', 'trader-joes': 'Trader Joe’s' };
	$: filterSummary = [
	  PRICE_LABELS[pricePoint] || 'Any price',
	  STORE_LABELS[storePreference] || 'All stores',
	  crueltyFree ? 'Cruelty-free only' : null
	].filter(Boolean).join(' · ');
  
	// Save and Load filters from local storage
	function saveFilters() {
	  localStorage.setItem('crueltyFree', crueltyFree);
	  localStorage.setItem('pricePoint', pricePoint);
	  localStorage.setItem('storePreference', storePreference);
	}
  
	function loadFilters() {
	  const storedCrueltyFree = localStorage.getItem('crueltyFree');
	  const storedPricePoint = localStorage.getItem('pricePoint');
	  const storedStorePreference = localStorage.getItem('storePreference');
	  
	  // Only set to true if explicitly stored as 'true', otherwise default to false
	  crueltyFree = storedCrueltyFree === 'true' ? true : false;
	  pricePoint = storedPricePoint || 'any';
	  storePreference = storedStorePreference || ''; // Default to both stores
	  
	  console.log('Loaded filters:', { crueltyFree, pricePoint, storePreference });
	}
  
	// Build request body by merging saved form data + filters
	function buildRequestBody() {
	  const form = get(formDataStore) || {};
	  console.log('Form data from store:', form);
	  console.log('Current filters:', { crueltyFree, pricePoint, storePreference });
	  
	  const payload = {
		...form,
		crueltyFree,
		pricePoint,
		storePreference,
	  };
	  
	  console.log('Final payload:', payload);
	  return payload;
	}
  
	// Fetch recommendations
	async function fetchRecommendations() {
	  const payload = buildRequestBody();
	  
	  // Validate that we have required fields
	  if (!payload.areasOfImprovement || !payload.age || !payload.skinType || !payload.routineType) {
		console.error('Missing required fields for recommendation request');
		alert('Please complete the user form first before applying filters.');
		return;
	  }
	  
	  try {
		const response = await fetch('/api/recommendation_engine', {
		  method: 'POST',
		  headers: { 'Content-Type': 'application/json' },
		  body: JSON.stringify(payload)
		});
		if (response.ok) {
		  const data = await response.json();
		  console.log('🔥 New recommendations received:', data);
		  console.log('🔥 Number of recommendations:', data.length);
		  console.log('🔥 First product name:', data[0]?.ProductName);
		  console.log('🔥 Before store update - current store value:', get(recommendations));
		  // update the parent-passed store
		  recommendations.set(data);
		  localStorage.setItem('activeRoutineRecommendations', JSON.stringify(data));
		  console.log('🔥 After store update - new store value:', get(recommendations));
		  
		  // Force a re-render by triggering store reactivity
		  recommendations.set([...data]);
		} else {
		  const errorText = await response.text();
		  console.error('Filter request failed:', response.status, errorText);
		  alert(`Filter request failed: ${errorText}`);
		}
	  } catch (error) {
		console.error('Error fetching recommendations:', error);
		alert('Network error while fetching recommendations');
	  }
	}
  
	// Fetch routine (button action)
	async function fetchRoutine() {
	  try {
		const response = await fetch('/api/routine', {
		  method: 'POST',
		  headers: { 'Content-Type': 'application/json' },
		  body: JSON.stringify(buildRequestBody())
		});
		if (response.ok) {
		  const data = await response.json();
		  regimenStore.set(data);
		  localStorage.setItem('activeRegimen', JSON.stringify(data));
		  goToRoutine(data);
		}
	  } catch (error) {
		console.error('Error fetching routine:', error);
	  }
	}

	// Which product a person actually opens is the only quality signal this
	// app has ever been able to collect. Until now every slot returned exactly
	// one product, so there was nothing to choose between and nothing to learn
	// from. Fire and forget: a failed log must never cost the user the click.
	function recordChoice(rec, rank) {
	  try {
		navigator.sendBeacon?.(
		  '/api/reco-click',
		  new Blob([JSON.stringify({
			productId: rec.ID,
			productType: rec.ProductType,
			productName: rec.ProductName,
			brand: rec.BrandName,
			price: rec.Price,
			rank,
		  })], { type: 'application/json' })
		);
	  } catch (e) {
		// Never let analytics break a product link.
	  }
	}

	function toggleAlternatives(id) {
	  openAlternatives = openAlternatives === id ? null : id;
	}

	function navigateToScanner() {
	  navigate('scanner');
	}

	function navigateToProfile() {
	  navigate('profile');
	}

	function navigateToRoutineImport() {
	  navigate('routine-import');
	}

	function formatLabel(value) {
	  return String(value || '')
		.replace(/_/g, ' ')
		.replace(/\b\w/g, letter => letter.toUpperCase());
	}

	// `[^\w\s-]` stripped every percent sign, plus sign, slash and decimal
	// point, so "Niacinamide 10% + Zinc 1%" was rendered as "Niacinamide 10
	// Zinc 1" on the one screen where the concentration is the product. Strip
	// the characters that are actually noise in a scraped title instead.
	function cleanProductName(name) {
	  return String(name || '')
	    .replace(/[\u2122\u00ae\u00a9]/g, '')      // trademark marks
	    .replace(/\s*[|\u2013\u2014]\s*$/,'')      // a dangling separator
	    .replace(/\s{2,}/g, ' ')
	    .trim();
	}

	function formatPrice(price) {
	  const numericPrice = Number(price);
	  if (Number.isNaN(numericPrice)) return price;
	  return `$${numericPrice.toFixed(numericPrice % 1 === 0 ? 0 : 2)}`;
	}

	function formatRating(rating) {
	  const numericRating = Number(rating);
	  if (Number.isNaN(numericRating)) return rating;
	  return numericRating.toFixed(1);
	}

	function routineRole(productType) {
	  const roles = {
		cleanser: 'Start here',
		toner: 'Prep step',
		essence: 'Hydration layer',
		serum: 'Treatment step',
		treatment: 'Treatment step',
		spot_treatment: 'Targeted treatment',
		retinol: 'Night treatment',
		eye_cream: 'Eye care',
		moisturizer: 'Seal it in',
		night_cream: 'Night moisturizer',
		oil: 'Final seal',
		sunscreen: 'Morning protection',
		exfoliant: 'Use a few times weekly',
		face_mask: 'Weekly support',
		mist: 'Optional refresh'
	  };
	  return roles[productType] || 'Routine step';
	}

	function concernList(rec) {
	  return rec.AreasOfImprovementAddressed || [];
	}

	function ingredientList(rec) {
	  const explained = rec.AOIExplained || {};
	  return [...new Set(Object.values(explained).flat())].slice(0, 5);
	}

	function fitReason(rec) {
	  const concerns = concernList(rec).map(formatLabel);
	  const ingredients = ingredientList(rec);
	  if (concerns.length && ingredients.length) {
		return `Targets ${concerns.slice(0, 2).join(' and ')} with ingredients like ${ingredients.slice(0, 2).join(' and ')}.`;
	  }
	  if (concerns.length) {
		return `Recommended because it lines up with ${concerns.slice(0, 2).join(' and ')}.`;
	  }
	  return 'Recommended as a strong fit for this step in your routine.';
	}
  
	// Load filters on mount and fetch recommendations
	onMount(() => {
	  // Clear any stale filter state first
	  localStorage.removeItem('crueltyFree');
	  localStorage.removeItem('pricePoint');
	  localStorage.removeItem('storePreference');
	  
	  // Set defaults
	  crueltyFree = false;
	  pricePoint = 'any';
	  storePreference = ''; // Default to both stores
	  
	  console.log('OnMount - reset filters to defaults:', { crueltyFree, pricePoint, storePreference });
	  console.log('OnMount - form data store:', get(formDataStore));
	  console.log('OnMount - current recommendations:', get(recommendations));
	  
	  const current = get(recommendations);
	  if (current && current.length > 0) {
		localStorage.setItem('activeRoutineRecommendations', JSON.stringify(current));
	  }
	  if (!current || current.length === 0) {
		// Only fetch if we have form data available
		if (get(formDataStore) && get(formDataStore).areasOfImprovement) {
		  fetchRecommendations();
		} else {
		  console.log('No form data available, skipping initial fetch');
		  // Don't alert - recommendations may be loading from post-auth flow
		}
	  }
	});
  </script>
  
  <style>
	/* Dark Editorial - My Routine
	   The products are the subject of this screen and own the full content
	   measure. Filters are a control surface: one hairline bar above the
	   grid, collapsed to a single line that still reads out what is applied.
	   Nothing here needs a media query; the two grids are intrinsic. */

	.page-head {
	  margin: 0 0 var(--space-12);
	}

	h1 {
	  font-family: var(--font-display);
	  font-size: var(--text-4xl);
	  font-weight: var(--weight-regular);
	  line-height: var(--leading-snug);
	  color: var(--cream);
	  margin: 0 0 var(--space-4);
	}

	.routine-intro {
	  max-width: var(--prose-max);
	  margin: 0;
	  color: var(--text-muted);
	  font-size: var(--text-md);
	  line-height: var(--leading-relaxed);
	}

	/* --- Next steps ------------------------------------------------------ */

	.section-head {
	  display: flex;
	  align-items: baseline;
	  justify-content: space-between;
	  gap: var(--space-4);
	  flex-wrap: wrap;
	  margin: 0 0 var(--space-4);
	  border-bottom: 1px solid var(--border);
	  padding-bottom: var(--space-3);
	}

	.eyebrow {
	  margin: 0;
	  font-size: var(--text-2xs);
	  font-weight: var(--weight-medium);
	  letter-spacing: var(--track-eyebrow);
	  text-transform: uppercase;
	  color: var(--text-muted);
	}

	/* Three next steps, three tracks. "View profile" is navigation, not a
	   next step, so it sits in the section head instead of orphaning a tile. */
	.next-actions {
	  display: grid;
	  grid-template-columns: repeat(auto-fit, minmax(min(240px, 100%), 1fr));
	  gap: var(--space-px);
	  background: var(--border);
	  border: 1px solid var(--border);
	  margin: 0 0 var(--space-16);
	}

	.next-action {
	  appearance: none;
	  background: var(--surface);
	  border: 0;
	  border-radius: var(--radius-0);
	  color: var(--cream);
	  min-height: 128px;
	  margin: 0;
	  padding: var(--space-6);
	  text-align: left;
	  cursor: pointer;
	  font-family: var(--font-body);
	  transition: background var(--transition), color var(--transition);
	}

	.next-action:hover {
	  background: var(--surface-2);
	  color: var(--rose);
	}

	.next-action strong {
	  display: block;
	  font-size: var(--text-2xs);
	  letter-spacing: var(--track-label);
	  text-transform: uppercase;
	  font-weight: var(--weight-medium);
	  margin-bottom: var(--space-3);
	}

	.next-action span {
	  display: block;
	  color: var(--text-muted);
	  font-size: var(--text-sm);
	  line-height: var(--leading-normal);
	  letter-spacing: var(--track-none);
	  text-transform: none;
	}

	/* --- Filter bar ------------------------------------------------------ */

	.filters {
	  border: 1px solid var(--border);
	  background: var(--surface);
	  margin: 0 0 var(--space-8);
	}

	.filters summary {
	  display: flex;
	  align-items: center;
	  gap: var(--space-4);
	  padding: var(--space-4) var(--space-6);
	  cursor: pointer;
	  list-style: none;
	  color: var(--text-muted);
	  font-size: var(--text-2xs);
	  letter-spacing: var(--track-label);
	  text-transform: uppercase;
	  transition: color var(--transition);
	}

	.filters summary::-webkit-details-marker { display: none; }
	.filters summary:hover { color: var(--cream); }

	.filters-title { color: var(--cream); }

	.filters-readout {
	  flex: 1;
	  min-width: 0;
	  color: var(--text-muted);
	  letter-spacing: var(--track-none);
	  text-transform: none;
	  font-size: var(--text-sm);
	}

	.chev {
	  flex: none;
	  font-size: var(--text-lg);
	  line-height: 1;
	  transition: transform var(--transition);
	}

	.filters[open] .chev { transform: rotate(45deg); }

	.filters-body {
	  display: grid;
	  grid-template-columns: repeat(auto-fit, minmax(min(200px, 100%), 1fr));
	  gap: var(--space-8);
	  align-items: start;
	  padding: var(--space-2) var(--space-6) var(--space-6);
	  border-top: 1px solid var(--border);
	}

	.filter-group h2 {
	  margin: var(--space-4) 0 var(--space-3);
	  font-family: var(--font-body);
	  font-size: var(--text-2xs);
	  font-weight: var(--weight-medium);
	  letter-spacing: var(--track-label);
	  text-transform: uppercase;
	  color: var(--cream);
	}

	.filter-group label {
	  display: flex;
	  align-items: center;
	  gap: var(--space-2);
	  margin: 0 0 var(--space-2);
	  color: var(--text-muted);
	  font-size: var(--text-sm);
	  cursor: pointer;
	  transition: color var(--transition);
	}

	.filter-group label:hover { color: var(--cream); }

	.filter-group input {
	  flex: none;
	  margin: 0;
	  padding: 0;
	  accent-color: var(--rose);
	}

	.filter-apply {
	  align-self: end;
	  padding-bottom: var(--space-2);
	}

	/* --- Product grid ---------------------------------------------------- */

	.results-head {
	  display: flex;
	  align-items: baseline;
	  justify-content: space-between;
	  gap: var(--space-4);
	  flex-wrap: wrap;
	  margin: 0 0 var(--space-5);
	}

	.cards {
	  display: grid;
	  grid-template-columns: repeat(auto-fit, minmax(min(420px, 100%), 1fr));
	  gap: var(--space-5);
	}

	.product {
	  display: flex;
	  flex-direction: column;
	  gap: var(--space-5);
	  height: 100%;
	}

	.product-media {
	  height: 200px;
	  overflow: hidden;
	  display: flex;
	  align-items: center;
	  justify-content: center;
	  background: var(--bg);
	  border: 1px solid var(--border);
	}

	.product-media img {
	  max-width: 100%;
	  max-height: 100%;
	  object-fit: contain;
	}

	.role {
	  margin: 0 0 var(--space-2);
	  color: var(--text-muted);
	  font-size: var(--text-2xs);
	  font-weight: var(--weight-medium);
	  letter-spacing: var(--track-label);
	  text-transform: uppercase;
	}

	.product h3 {
	  font-family: var(--font-display);
	  font-size: var(--text-xl);
	  color: var(--cream);
	  margin: 0 0 var(--space-2);
	  line-height: var(--leading-snug);
	  font-weight: var(--weight-regular);
	}

	.brand {
	  margin: 0;
	  color: var(--text-muted);
	  font-size: var(--text-sm);
	}

	.markers {
	  display: flex;
	  flex-wrap: wrap;
	  gap: var(--space-2);
	}

	/* The reason is the argument this product is here at all, so it is the
	   largest body text in the card, in full-strength ink, with the accent
	   rule marking it as SkinGenie speaking rather than the retailer. */
	.why {
	  border-left: 1px solid var(--rose-line);
	  padding-left: var(--space-5);
	}

	.why-label {
	  margin: 0 0 var(--space-2);
	  color: var(--rose);
	  font-size: var(--text-2xs);
	  font-weight: var(--weight-medium);
	  letter-spacing: var(--track-label);
	  text-transform: uppercase;
	}

	.why-text {
	  margin: 0;
	  color: var(--text);
	  font-size: var(--text-md);
	  line-height: var(--leading-relaxed);
	}

	.why-ingredients {
	  margin: var(--space-3) 0 0;
	  color: var(--text-muted);
	  font-size: var(--text-sm);
	  line-height: var(--leading-normal);
	}

	.product-foot {
	  display: flex;
	  align-items: flex-end;
	  justify-content: space-between;
	  gap: var(--space-5);
	  flex-wrap: wrap;
	  margin-top: auto;
	  padding-top: var(--space-5);
	  border-top: 1px solid var(--border);
	}

	.rating-note {
	  margin: var(--space-2) 0 0;
	  color: var(--text-muted);
	  font-size: var(--text-xs);
	}

	.unavailable {
	  display: flex;
	  flex-direction: column;
	  align-items: flex-start;
	  gap: var(--space-4);
	}

	.unavailable h3 {
	  font-family: var(--font-display);
	  font-size: var(--text-lg);
	  font-weight: var(--weight-regular);
	  color: var(--cream);
	  margin: 0;
	}

	.unavailable p {
	  margin: 0;
	  color: var(--text-muted);
	  font-size: var(--text-sm);
	}

	/* Alternatives: the engine already scored the whole pool and kept a top
	   few. Quiet by default, because the point is still that SkinGenie made a
	   choice, not that it handed over a search results page. */
	.alts {
		margin-top: var(--space-4);
		padding-top: var(--space-3);
		border-top: 1px solid var(--border);
	}

	.alts-toggle {
		background: none;
		border: 0;
		padding: 0;
		margin: 0;
		color: var(--text-muted);
		font-family: var(--font-body);
		font-size: var(--text-2xs);
		letter-spacing: var(--track-label);
		text-transform: uppercase;
		cursor: pointer;
		transition: color var(--transition);
	}

	.alts-toggle:hover { color: var(--cream); }

	.alt-list {
		list-style: none;
		margin: var(--space-3) 0 0;
		padding: 0;
		display: grid;
		gap: 1px;
		background: var(--border);
	}

	.alt {
		display: grid;
		grid-template-columns: minmax(0, 1fr) auto auto;
		gap: var(--space-3);
		align-items: baseline;
		padding: var(--space-3) 0;
		background: var(--surface);
	}

	.alt-name { min-width: 0; font-size: var(--text-sm); line-height: var(--leading-snug); }

	.alt-brand {
		display: block;
		font-size: var(--text-3xs);
		letter-spacing: var(--track-label);
		text-transform: uppercase;
		color: var(--text-muted);
	}

	.conflict {
	  margin: 0;
	  padding: var(--space-3) var(--space-4);
	  border-radius: var(--radius-sm);
	  background: var(--caution-bg);
	  color: var(--text);
	  font-size: var(--text-sm);
	  line-height: var(--leading-snug);
	}

	.alt-conflict {
	  display: block;
	  margin-top: var(--space-1);
	  font-size: var(--text-2xs);
	  color: var(--caution);
	}

	.alt-price { font-size: var(--text-sm); color: var(--cream); font-variant-numeric: tabular-nums; }

	.alt-link {
		font-size: var(--text-2xs);
		letter-spacing: var(--track-label);
		text-transform: uppercase;
		color: var(--rose);
	}
</style>
  
  <div class="u-page">
	<header class="page-head">
	  {#if specificMode}
		<h1>Picked for you</h1>
		<p class="routine-intro">The products you asked for, ranked for your skin.{checkedAgainstRoutine ? ' Each one was checked against the routine you already use; any that clash with it come last, with a note.' : ''}</p>
	  {:else}
		<h1>My Routine</h1>
		<p class="routine-intro">A buyable routine built from your skin profile{checkedAgainstRoutine ? ' and checked against what you already use' : ''}. Use Check Product before buying anything new, or turn this into a morning and night plan when you are ready.</p>
	  {/if}
	</header>

	<div class="section-head">
	  <p class="eyebrow">Next steps</p>
	  <Button variant="ghost" size="sm" on:click={navigateToProfile}>View profile</Button>
	</div>
	<div class="next-actions" aria-label="Next steps">
	  <button class="next-action" type="button" on:click={navigateToScanner}>
		<strong>Check a Product</strong>
		<span>Look up something from TikTok, Sephora, Ulta, Amazon, or your shelf.</span>
	  </button>
	  <button class="next-action" type="button" on:click={fetchRoutine}>
		<strong>Morning &amp; Night Plan</strong>
		<span>Turn these products into a morning and evening plan.</span>
	  </button>
	  <button class="next-action" type="button" on:click={navigateToRoutineImport}>
		<strong>Add Current Routine</strong>
		<span>Import what you already use so SkinGenie recommends smaller, smarter changes.</span>
	  </button>
	</div>

	<div class="results-head">
	  <p class="eyebrow">Your products</p>
	</div>

	<details class="filters">
	  <summary>
		<span class="filters-title">Filters</span>
		<span class="filters-readout">{filterSummary}</span>
		<span class="chev" aria-hidden="true">+</span>
	  </summary>
	  <div class="filters-body">
		<div class="filter-group">
		  <h2>Price</h2>
		  <label>
			<input type="radio" name="pricePoint" value="any" bind:group={pricePoint} on:change={saveFilters} /> Any price
		  </label>
		  <label>
			<input type="radio" name="pricePoint" value="low" bind:group={pricePoint} on:change={saveFilters} /> Low (&lt; $50)
		  </label>
		  <label>
			<input type="radio" name="pricePoint" value="medium" bind:group={pricePoint} on:change={saveFilters} /> Medium ($50-100)
		  </label>
		  <label>
			<input type="radio" name="pricePoint" value="high" bind:group={pricePoint} on:change={saveFilters} /> High (&gt; $100)
		  </label>
		</div>

		<div class="filter-group">
		  <h2>Store</h2>
		  <label>
			<input type="radio" name="storePreference" value="" bind:group={storePreference} on:change={saveFilters} /> All stores
		  </label>
		  <label>
			<input type="radio" name="storePreference" value="sephora" bind:group={storePreference} on:change={saveFilters} /> Sephora
		  </label>
		  <label>
			<input type="radio" name="storePreference" value="ulta" bind:group={storePreference} on:change={saveFilters} /> Ulta Beauty
		  </label>
		  <label>
			<input type="radio" name="storePreference" value="amazon" bind:group={storePreference} on:change={saveFilters} /> Amazon
		  </label>
		  <label>
			<input type="radio" name="storePreference" value="trader-joes" bind:group={storePreference} on:change={saveFilters} /> Trader Joe’s
		  </label>
		</div>

		<div class="filter-group">
		  <h2>Brand values</h2>
		  <label>
			<input type="checkbox" bind:checked={crueltyFree} on:change={saveFilters} /> Cruelty-free only
		  </label>
		</div>

		<div class="filter-apply">
		  <Button variant="secondary" size="sm" full on:click={fetchRecommendations}>Apply filters</Button>
		</div>
	  </div>
	</details>

	{#if $recommendations && $recommendations.length > 0}
	  <div class="cards">
		{#each $recommendations as rec, cardIndex}
		  {#if rec.ProductName && rec.BrandName && rec.Price}
			<Card interactive>
			  <article class="product">
				{#if rec.ImageUrl}
				  <div class="product-media">
					<img src={rec.ImageUrl} alt={rec.ProductName} on:error={(e) => e.target.style.display='none'} />
				  </div>
				{/if}
				<div>
				  <p class="role">{routineRole(rec.ProductType)}</p>
				  <h3>{cleanProductName(rec.ProductName)}</h3>
				  <p class="brand">{rec.BrandName}</p>
				</div>
				<div class="markers">
				  <Chip>{formatLabel(rec.ProductType)}</Chip>
				  {#each concernList(rec) as aoi}
					<Chip>{formatLabel(aoi)}</Chip>
				  {/each}
				</div>
				{#if rec.RoutineConflict}
				  <p class="conflict">With your routine: {rec.RoutineConflict.charAt(0).toLowerCase() + rec.RoutineConflict.slice(1)}.</p>
				{/if}
				<div class="why">
				  <p class="why-label">Why SkinGenie picked this</p>
				  <p class="why-text">{fitReason(rec)}</p>
				  {#if ingredientList(rec).length > 0}
					<p class="why-ingredients">Helpful ingredients: {ingredientList(rec).join(', ')}</p>
				  {/if}
				</div>
				<div class="product-foot">
				  <div>
					<Stat value={formatPrice(rec.Price)} label="Price" align="start" />
					<p class="rating-note">Rated {formatRating(rec.Rating)} from {rec.Reviews} reviews</p>
				  </div>
				  <Button
					size="sm"
					href={rec.SourceLink}
					target="_blank"
					rel="noopener noreferrer"
					on:click={() => recordChoice(rec, 0)}
				  >View product</Button>
				</div>

				{#if rec.Alternatives && rec.Alternatives.length > 0}
				  <div class="alts">
					<button
					  class="alts-toggle"
					  aria-expanded={openAlternatives === cardIndex}
					  on:click={() => toggleAlternatives(cardIndex)}
					>
					  {openAlternatives === cardIndex ? 'Hide' : 'Show'} {rec.Alternatives.length} other
					  {formatLabel(rec.ProductType).toLowerCase()}{rec.Alternatives.length === 1 ? '' : 's'}
					  that fit
					</button>
					{#if openAlternatives === cardIndex}
					  <ul class="alt-list">
						{#each rec.Alternatives as alt, i}
						  <li class="alt">
							<div class="alt-name">
							  <span class="alt-brand">{alt.BrandName}</span>
							  <span>{cleanProductName(alt.ProductName)}</span>
							  {#if alt.RoutineConflict}
								<span class="alt-conflict">With your routine: {alt.RoutineConflict.charAt(0).toLowerCase() + alt.RoutineConflict.slice(1)}.</span>
							  {/if}
							</div>
							<span class="alt-price">{formatPrice(alt.Price)}</span>
							<a
							  class="alt-link"
							  href={alt.SourceLink}
							  target="_blank"
							  rel="noopener noreferrer"
							  on:click={() => recordChoice(alt, i + 1)}
							>View</a>
						  </li>
						{/each}
					  </ul>
					{/if}
				  </div>
				{/if}
			  </article>
			</Card>
		  {:else}
			<!-- Skip malformed recommendation objects -->
			<Card tone="caution">
			  <div class="unavailable">
				<h3>Product not available</h3>
				<p>
				  {rec.message || 'No recommendation found'}
				  {#if rec.productType}for {rec.productType}{/if}
				</p>
				<Button variant="secondary" size="sm" on:click={navigateToScanner}>Check another product</Button>
			  </div>
			</Card>
		  {/if}
		{/each}
	  </div>
	{:else}
	  <EmptyState
		title="No products in your routine yet."
		body="SkinGenie builds a buyable routine once it knows what your skin is working on. Import what you already use and it will start from there, then suggest smaller, smarter changes.">
		<Button on:click={navigateToRoutineImport}>Add current routine</Button>
	  </EmptyState>
	{/if}
  </div>
